import React, { useState, useEffect, useCallback } from 'react';
import planejamentoService from '../../services/planejamento.service';
import PlanejamentoModal   from '../../components/PlanejamentoModal';
import SePode              from '../../components/SePode';   // r88
import BrowseHeader        from '../../components/BrowseHeader';                    // r111
import { iconeDaRotina }   from '../../constants/iconesRotinas';               // r111

// ════════════════════════════════════════════════════════════════════════════
// Constantes
// ════════════════════════════════════════════════════════════════════════════
const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#1D9E75',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '14px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

const TIPOS = [
  { v: 'P', l: 'Projeto'      },
  { v: 'U', l: 'Pontual'      },
  { v: 'S', l: 'Service Desk' },
  { v: 'T', l: 'Sustentação'  },
];

// Etapa TÉCNICA — todas, incluindo Encerrado.
const ETAPAS_TEC = [
  { v: 'Planejamento',      l: 'Planejamento',      cor: '#6b7280', bg: '#f3f4f6' },
  { v: 'Execução',          l: 'Execução',          cor: '#0ea5e9', bg: '#e0f2fe' },
  { v: 'Qualidade',         l: 'Qualidade',         cor: '#a855f7', bg: '#f3e8ff' },
  { v: 'Validação Cliente', l: 'Validação Cliente', cor: '#f59e0b', bg: '#fef3c7' },
  { v: 'Acompanhamento',    l: 'Acompanhamento',    cor: '#06b6d4', bg: '#cffafe' },
  { v: 'Encerrado',         l: 'Encerrado',         cor: '#059669', bg: '#d1fae5' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const labelTipo = (v) => TIPOS.find(t => t.v === v)?.l || v;
const etapaTec  = (v) => ETAPAS_TEC.find(e => e.v === v) || { l: v, cor: '#6b7280', bg: '#f3f4f6' };

const formatarDataBR = (iso) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const diasDesde = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const dt = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]), 12, 0, 0);
  const hoje = new Date(); hoje.setHours(12, 0, 0, 0);
  return Math.max(0, Math.round((hoje.getTime() - dt.getTime()) / 86400000));
};

const corDias = (dias) => {
  if (dias === null) return { bg: '#f3f4f6', cor: '#6b7280', borda: '#d1d5db' };
  if (dias < 7)      return { bg: '#f0fff4', cor: '#1D9E75', borda: '#1D9E7555' };
  if (dias <= 15)    return { bg: '#fffbeb', cor: '#d97706', borda: '#d9770655' };
  return                    { bg: '#fff0f0', cor: '#e53935', borda: '#e5393555' };
};

// ─── Componentes auxiliares ─────────────────────────────────────────────────
function TagCliente({ tag, cor }) {
  if (!tag) return <span style={{ color: COR.muted, fontSize: '12px' }}>—</span>;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0,2),16);
  const g = parseInt(hex.substr(2,2),16);
  const b = parseInt(hex.substr(4,2),16);
  const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: bgCor, color: corTexto, letterSpacing: '0.3px',
    }}>{tag}</span>
  );
}

function EtapaTecPill({ etapa }) {
  const e = etapaTec(etapa);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: e.bg, color: e.cor,
      border: `1px solid ${e.cor}`,           // r111: borda sólida na cor da fonte
    }}>{e.l}</span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
function PlanejamentoPage() {
  const [lista, setLista]           = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca]           = useState('');
  const [ordenacao, setOrdenacao]   = useState({ campo: 'id', dir: 'asc' });

  // Filtros default: só Tipo=Projeto e Etapa Técnica=Planejamento
  const [filtroTipos, setFiltroTipos]     = useState(new Set(['P']));
  const [filtroEtapasTec, setFiltroEtapasTec] = useState(new Set(['Planejamento']));

  // Modal de Planejamento (sessão Projeto + Horas + Analistas)
  const [modalAberto, setModalAberto]   = useState(false);
  const [projetoIdAberto, setProjetoIdAberto] = useState(null);

  // Usuário logado (lido do localStorage) — passado pro modal pra auditoria
  const usuarioLogado = (() => {
    try {
      const raw = localStorage.getItem('usuario');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  })();

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await planejamentoService.listar();
      setLista(dados);
    } catch (err) {
      console.error('Erro ao carregar planejamento:', err);
      setLista([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ═══ Filtro + Ordenação ═══
  const listaFiltrada = lista
    .filter(n => {
      const q = busca.toLowerCase();
      const matchBusca = !q
        || (n.id || '').toLowerCase().includes(q)
        || (n.descricao || '').toLowerCase().includes(q)
        || (n.cliente_nome || '').toLowerCase().includes(q)
        || (n.resp_tecnico_nome || '').toLowerCase().includes(q);
      const matchTipo     = filtroTipos.has(n.tipo);
      const matchEtapaTec = filtroEtapasTec.has(n.etapa_tecnica);
      return matchBusca && matchTipo && matchEtapaTec;
    })
    .sort((a, b) => {
      const va = (a[ordenacao.campo] ?? '').toString().toLowerCase();
      const vb = (b[ordenacao.campo] ?? '').toString().toLowerCase();
      return ordenacao.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const toggleOrdem = (campo) =>
    setOrdenacao(o => ({ campo, dir: o.campo === campo && o.dir === 'asc' ? 'desc' : 'asc' }));

  const setaCabecalho = (campo) => {
    if (ordenacao.campo !== campo) return ' ↕';
    return ordenacao.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleSet = (set, setter, valor) => {
    setter(s => { const n = new Set(s); n.has(valor) ? n.delete(valor) : n.add(valor); return n; });
  };
  const toggleAllSet = (set, setter, todos) => {
    setter(set.size === todos.length ? new Set() : new Set(todos));
  };

  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ padding: '6px 20px 20px' }}>
      {/* r111: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="planejamento"
        titulo="Planejamento de Projeto"
        descricao="Acompanhamento técnico dos negócios fechados em execução, validação e encerramento."
      />

      {/* ═══════════ BROWSE (mesmo padrão CRM Negócios) ═══════════ */}
      <div style={{
        flex: 1, backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden', minWidth: 0, marginBottom: '12px',
      }}>
        {/* Header interno do browse: contagem + busca à direita */}
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: COR.muted, flex: 1 }}>
            {listaFiltrada.length} de {lista.length} registro{lista.length !== 1 ? 's' : ''}
          </span>
          <input
            type="text" placeholder="Pesquisar..."
            value={busca} onChange={e => setBusca(e.target.value)}
            style={{ ...inputStyle, width: '220px' }}
          />
        </div>

        {/* Tabela */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.azul }}>
                {/* r111: 1ª coluna fixa pro ícone SVG da rotina */}
                <th style={{ padding: '10px 14px', width: '36px' }}></th>
                {[
                  { label: 'Código',          campo: 'id'                       },
                  { label: 'Descrição',       campo: 'descricao'                },
                  { label: 'Cliente',         campo: 'cliente_tag'              },
                  { label: 'Tipo',            campo: 'tipo'                     },
                  { label: 'Etapa',           campo: 'etapa_tecnica'            },
                  { label: 'Data fechamento', campo: 'dt_fechamento'            },
                  { label: 'Dias etapa',      campo: 'dt_entrada_etapa_tecnica' },
                ].map(col => (
                  <th key={col.campo}
                    onClick={() => toggleOrdem(col.campo)}
                    style={{
                      padding: '10px 14px', textAlign: 'left',
                      color: COR.branco, fontWeight: '700',
                      fontSize: '13px',
                      cursor: 'pointer', userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >{col.label}{setaCabecalho(col.campo)}</th>
                ))}
                <th style={{
                  padding: '10px 14px', textAlign: 'left',
                  color: COR.branco, fontWeight: '700',
                  fontSize: '13px',
                }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                    Carregando...
                  </td>
                </tr>
              ) : listaFiltrada.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                    Nenhum projeto encontrado com os filtros atuais.
                  </td>
                </tr>
              ) : (
                listaFiltrada.map(n => {
                  const dias = diasDesde(n.dt_entrada_etapa_tecnica);
                  const cores = corDias(dias);
                  return (
                    <tr key={n.id} style={{ borderBottom: `1px solid ${COR.borda}` }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.cinzaBg}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}
                    >
                      {/* r111: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                      <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                        {iconeDaRotina('planejamento', { width: 20, height: 20, color: COR.azul })}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: '600', color: COR.azul, whiteSpace: 'nowrap' }}>
                        {n.id}
                      </td>
                      <td style={{ padding: '10px 14px', color: COR.texto, maxWidth: '320px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {n.descricao}
                        </div>
                        <div style={{ fontSize: '11px', color: COR.muted, marginTop: '2px' }}>
                          {n.cliente_razao_social || n.cliente_nome}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <TagCliente tag={n.cliente_tag} cor={n.cliente_cor_tag} />
                      </td>
                      <td style={{ padding: '10px 14px', color: COR.muted, fontSize: '12px' }}>
                        {labelTipo(n.tipo)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <EtapaTecPill etapa={n.etapa_tecnica} />
                      </td>
                      <td style={{ padding: '10px 14px', color: COR.muted, fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {formatarDataBR(n.dt_fechamento)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span title={dias === null ? 'Sem registro de entrada' : `${dias} dia${dias === 1 ? '' : 's'} desde a entrada na etapa`} style={{
                          display: 'inline-block',
                          padding: '2px 10px', borderRadius: '20px',
                          fontSize: '12px', fontWeight: '500',
                          backgroundColor: cores.bg, color: cores.cor,
                          border: `1px solid ${cores.cor}`,           // r111: borda sólida na cor da fonte
                          minWidth: '32px', textAlign: 'center',
                        }}>
                          {dias === null ? '—' : `${dias}d`}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                        <SePode rotina="planejamento" acao="alterar">
                        <button
                          type="button"
                          onClick={() => {
                            setProjetoIdAberto(n.id);
                            setModalAberto(true);
                          }}
                          title="Abrir planejamento detalhado"
                          style={{
                            padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                            border: `1px solid ${COR.azul}`,
                            backgroundColor: COR.branco, color: COR.azul,
                            borderRadius: '5px', cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >🛠️ Planejar</button>
                        </SePode>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════ PAINEL DE FILTROS (padrão CRM Negócios) ═══════════ */}
      {/* Mesmo wrapper branco do CRM, com 3 grupos lado a lado */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        padding: '14px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap',
      }}>
        {/* ── Filtro Tipo — 2 col × 2 linhas ── */}
        <div style={{
          minWidth: '180px',
          border: `1px solid ${COR.borda}`, borderRadius: '6px',
          padding: '10px 12px', backgroundColor: COR.cinzaBg,
        }}>
          <div
            onClick={() => toggleAllSet(filtroTipos, setFiltroTipos, TIPOS.map(t => t.v))}
            title="Clique para marcar/desmarcar todos"
            style={{
              fontSize: '11px', fontWeight: '700', color: COR.azul,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: '8px', cursor: 'pointer', userSelect: 'none',
            }}
          >Tipo</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: '12px', rowGap: '4px',
          }}>
            {TIPOS.map(t => (
              <label key={t.v} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: COR.texto,
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input type="checkbox" checked={filtroTipos.has(t.v)}
                  onChange={() => toggleSet(filtroTipos, setFiltroTipos, t.v)}
                  style={{ cursor: 'pointer' }} />
                {t.l}
              </label>
            ))}
          </div>
        </div>

        {/* ── Filtro Etapa Técnica — 3 col × 2 linhas ── */}
        <div style={{
          minWidth: '380px',
          border: `1px solid ${COR.borda}`, borderRadius: '6px',
          padding: '10px 12px', backgroundColor: COR.cinzaBg,
        }}>
          <div
            onClick={() => toggleAllSet(filtroEtapasTec, setFiltroEtapasTec, ETAPAS_TEC.map(e => e.v))}
            title="Clique para marcar/desmarcar todos"
            style={{
              fontSize: '11px', fontWeight: '700', color: COR.azul,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: '8px', cursor: 'pointer', userSelect: 'none',
            }}
          >Etapa Técnica</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            columnGap: '12px', rowGap: '4px',
          }}>
            {ETAPAS_TEC.map(e => (
              <label key={e.v} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: COR.texto,
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input type="checkbox" checked={filtroEtapasTec.has(e.v)}
                  onChange={() => toggleSet(filtroEtapasTec, setFiltroEtapasTec, e.v)}
                  style={{ cursor: 'pointer' }} />
                <span style={{
                  display: 'inline-block', width: '8px', height: '8px',
                  borderRadius: '50%', backgroundColor: e.cor,
                }}></span>
                {e.l}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de Planejamento (sessões Projeto / Horas / Analistas) */}
      <PlanejamentoModal
        aberto={modalAberto}
        projetoId={projetoIdAberto}
        usuario={usuarioLogado}
        onFechar={async () => {
          setModalAberto(false);
          setProjetoIdAberto(null);
          // Recarrega a lista pra refletir possíveis mudanças
          // (se a etapa técnica mudar no futuro, etc)
          await carregar();
        }}
      />
    </div>
  );
}

export default PlanejamentoPage;
